import { useState } from "react";

import Alert from "common/components/ui/Alert";
import { Button } from "common/components/ui/Button";
import { CountSpinner } from "common/components/ui/CountSpinner";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import Skeleton from "common/components/ui/Skeleton";
import { Tag } from "common/components/ui/Tag";
import { useSelectedWorkspaceContext } from "common/context/SelectedWorkspaceContext";
import useLicenseMutations from "common/datahooks/useLicenseMutations";
import usePromotions from "common/datahooks/usePromotions";
import useLicensedAccounts from "common/hooks/useLicensedAccounts";

import PlanStatCard from "../PlanStatCard";
import LicenseAccounts from "./LicenseAccounts";

export default function AllocateLtdSeats() {
  const [isOpen, setIsOpen] = useState(false);

  const { id: workspaceId, lifetime_license_count } =
    useSelectedWorkspaceContext();
  const { ltdPromotion, appSumoPromotion, isLoadingPromotions } =
    usePromotions();
  const promotion = ltdPromotion ?? appSumoPromotion;

  const { outreachLicensedAccounts } = useLicensedAccounts(workspaceId);
  const { setLifetimeLicenseCount, isSettingLifetimeLicenseCount } =
    useLicenseMutations();

  const [seatCount, setSeatCount] = useState(lifetime_license_count);

  const assignedCount = outreachLicensedAccounts.length;
  const unassignedLicenseCount = promotion?.unassigned_license_count ?? 0;
  const availableInPool =
    unassignedLicenseCount + (lifetime_license_count - seatCount);

  function handleOpenChange(open: boolean) {
    setIsOpen(open);
    if (open) {
      setSeatCount(lifetime_license_count);
    }
  }

  async function onSubmit() {
    if (!promotion || seatCount === lifetime_license_count) {
      return;
    }

    await setLifetimeLicenseCount({
      workspaceId,
      count: seatCount,
      source: promotion.type,
      accountsToCancel: [],
    });
    setIsOpen(false);
  }

  if (!promotion) {
    return null;
  }

  const isUpdateDisabled =
    seatCount === lifetime_license_count ||
    seatCount < assignedCount ||
    isSettingLifetimeLicenseCount;

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className="ml-auto" size="sm" variant="secondary-purple">
          Allocate seats
        </Button>
      </DialogTrigger>

      <DialogContent>
        <DialogHeader title="Allocate seats" />

        <DialogBody>
          <div className="mt-6 flex justify-center">
            <CountSpinner
              variant="lg"
              value={seatCount}
              setValue={setSeatCount}
              minValue={assignedCount}
              maxValue={lifetime_license_count + unassignedLicenseCount}
              minValueTooltipText="You must unassign all seats before you can remove them from this workspace."
              maxValueTooltipText="No more seats available"
            />
          </div>

          <div className="mt-4 grid grid-cols-2 gap-2">
            <PlanStatCard title="Assigned">
              <Tag variant="tertiary" size="md" className="w-fit">
                {assignedCount}
                <span className="text-black-500">/ {seatCount}</span>
              </Tag>
              <LicenseAccounts accounts={outreachLicensedAccounts} />
            </PlanStatCard>

            <PlanStatCard title="Available in pool">
              {isLoadingPromotions ? (
                <Skeleton className="h-6 w-10" />
              ) : (
                <Tag variant="success" size="md" className="w-fit">
                  {availableInPool}
                </Tag>
              )}
            </PlanStatCard>
          </div>

          {seatCount < assignedCount && (
            <Alert variant="warning" className="mt-4">
              Cannot remove seats assigned to accounts. Unassign seats to
              continue
            </Alert>
          )}

          <Button
            className="ml-auto mt-6"
            isLoading={isSettingLifetimeLicenseCount}
            disabled={isUpdateDisabled}
            onClick={onSubmit}
          >
            Update seats
          </Button>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
